import { makeAutoObservable } from 'mobx'
import { UserFactory } from './../../../shared/factories'
import appConstants from './../../../constants/appConstants'
import moment from 'moment'
import { getWeekMonday } from './getWeekMonday.function'

const fetchMaxAgeMeta = async User => {
  let UserMeta = ((await (await User.meta(true)).key('profile---max-av-age-months')) || []).shift()

  UserMeta = UserMeta
    ? UserMeta
    : await (
        await User.meta(true)
      ).create({
        meta_key: 'profile---max-av-age-months',
        meta_value: Math.floor(appConstants.av_tracking_date_limit / 30),
      })

  return UserMeta
}

const fetchBackDateMeta = async User => {
  let UserMeta = ((await (await User.meta(true)).key('profile---max-av-back-date')) || []).shift()

  UserMeta = UserMeta
    ? UserMeta
    : await (
        await User.meta(true)
      ).create({
        meta_key: 'profile---max-av-back-date',
        meta_value: null,
      })

  return UserMeta
}

const fetchEditBeyondYearMeta = async User => {
  let UserMeta = ((await (await User.meta(true)).key('profile---max-av-edit-beyond-year')) || []).shift()

  UserMeta = UserMeta
    ? UserMeta
    : await (
        await User.meta(true)
      ).create({
        meta_key: 'profile---max-av-edit-beyond-year',
        meta_value: 0,
      })

  return UserMeta
}

class MaxAvDateStore {
  User = null
  UserMeta = null
  BackDateMeta = null
  EditBeyondYearMeta = null
  ageSetting = null
  minEditableDate = null
  Policy = null
  isAgeRestricted = false
  isPreviousYearLocked = false
  isAgeMonthsLocked = false
  enableBackdate = false
  enableEditBeyondYear = false

  constructor() {
    makeAutoObservable(this)
  }

  setUser = async userId => {
    const User = userId && (await UserFactory.findById(userId))
    this.User = User
    return User
  }

  setMetas = async () => {
    if (this.User) {
      const [UserMeta, BackDateMeta, EditBeyondYearMeta] = await Promise.all([
        fetchMaxAgeMeta(this.User),
        fetchBackDateMeta(this.User),
        fetchEditBeyondYearMeta(this.User),
      ])
      this.UserMeta = UserMeta
      this.BackDateMeta = BackDateMeta
      this.EditBeyondYearMeta = EditBeyondYearMeta

      if (
        BackDateMeta?.get('updated_at') &&
        moment(BackDateMeta.get('updated_at')).isBetween(moment().subtract(72, 'hours'), moment())
      ) {
        this.enableBackdate =
          BackDateMeta?.get('meta_value') > 0 && !isNaN(BackDateMeta.get('meta_value'))
            ? new Date(parseInt(BackDateMeta.get('meta_value')))
            : false
      } else {
        this.enableBackdate = false
      }

      this.enableEditBeyondYear =
        EditBeyondYearMeta?.get('meta_value') === '1' || EditBeyondYearMeta?.get('meta_value') === 1

      return UserMeta
    }

    return
  }

  setAgeSetting = age => {
    this.ageSetting = !isNaN(age) && age > -1 ? parseInt(age) : 5
    this.setMinDate()
  }

  setMinDate = () => {
    this.minEditableDate = getWeekMonday(moment().subtract(this.ageSetting, 'month').toDate())
    return this.minEditableDate
  }

  resetDate = () => {
    this.UserMeta = null
    this.EditBeyondYearMeta = null
    this.setAgeSetting(5)
  }

  fetchMaxAvAge = async userId => {
    const User = await this.setUser(userId)
    if (!User) return this.resetDate()

    await this.setMetas()

    this.setAgeSetting(this.UserMeta?.get('meta_value'))

    this.determineIfAgeRestricted()

    return new Date()
  }

  saveAgeSetting = async () => {
    if (this.UserMeta) {
      this.UserMeta.set('meta_value', this.ageSetting)
      return await this.UserMeta.save()
    }

    return false
  }

  setPolicy = Policy => {
    this.Policy = Policy
    this.determineIfAgeRestricted()
  }

  isPolicyInPreviousYear = () => {
    const avDate = this.Policy?.get('av_date')
    if (!avDate) return false
    const policyM = moment(avDate)
    const nowM = moment()
    return policyM.year() < nowM.year()
  }

  isPolicyInSameIsoWeekAsNow = () => {
    const avDate = this.Policy?.get('av_date')
    if (!avDate) return false
    const policyM = moment(avDate)
    const nowM = moment()
    return policyM.isoWeek() === nowM.isoWeek() && policyM.isoWeekYear() === nowM.isoWeekYear()
  }

  determineIfAgeRestricted = () => {
    let ageRestricted = false
    if (this.minEditableDate) {
      ageRestricted =
        moment(this.Policy?.get('av_date') || new Date())
          .toDate()
          .getTime() < this.minEditableDate.getTime()
    } else {
      ageRestricted = true
    }
    const previousYear =
      this.isPolicyInPreviousYear() && !this.isPolicyInSameIsoWeekAsNow() && !this.enableEditBeyondYear
    this.isPreviousYearLocked = previousYear
    this.isAgeMonthsLocked = ageRestricted && !previousYear
    this.isAgeRestricted = ageRestricted || previousYear
  }

  setEnableBackdate = async enableBackdate => {
    if (!!enableBackdate) {
      this.BackDateMeta.set('meta_value', parseInt((this.enableBackdate = this.minEditableDate).getTime()))
      await this.BackDateMeta.save()
    } else {
      this.enableBackdate = false
      this.BackDateMeta.set('meta_value', false)
      await this.BackDateMeta.save()
    }
  }

  setEnableEditBeyondYear = async enable => {
    if (this.EditBeyondYearMeta) {
      this.enableEditBeyondYear = !!enable
      this.EditBeyondYearMeta.set('meta_value', enable ? 1 : 0)
      await this.EditBeyondYearMeta.save()
      this.determineIfAgeRestricted()
    }
  }
}

export default new MaxAvDateStore()
